import React, { useState, useEffect } from 'react';
import pb from '../pocketbase';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import './AdminServices.css';

const AdminServices = () => {
    const [tarifs, setTarifs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [serviceType, setServiceType] = useState('auto'); // 'auto', 'mobilier', 'option'
    const [editingId, setEditingId] = useState(null); // ID of tarif being edited

    // Define Categories based on Main Type (Matching PocketBase Schema Values)
    const autoCategories = ['Formule', 'Detailing', 'Interieur', 'Exterieur', 'Complet'];
    const mobilierCategories = ['Canapé', 'Matelas', 'Chaises', 'Tapis', 'Autre'];
    // Options don't really have sub-categories in the UI, we just label them "Option"
    const optionCategories = ['Option'];

    // Default structure for new price
    const defaultTarif = {
        titre: '',
        categorie: 'Formule', // Default
        prix_citadine: '',
        prix_berline: '',
        prix_suv: '',
        duree: '', // Duration in minutes
        ordre: 10,
        populaire: false,
        description: '', // Subtitle
        liste_details: '' // Feature list
    };
    const [formData, setFormData] = useState(defaultTarif);

    useEffect(() => {
        loadTarifs();
    }, []);

    const loadTarifs = async () => {
        try {
            const records = await pb.collection('tarifs').getList(1, 100, { 
                sort: 'ordre,titre',
                requestKey: null 
            });
            setTarifs(records.items);
            setLoading(false);
        } catch (err) {
            console.error("Erreur chargement tarifs", err);
            setLoading(false);
        }
    };

    const handleResetForm = (type = serviceType) => {
        setEditingId(null);
        let defaultCat = 'Formule';
        if (type === 'mobilier') defaultCat = 'Canapé';
        if (type === 'option') defaultCat = 'Option';

        setFormData({ ...defaultTarif, categorie: defaultCat });
    };

    const handleEdit = (tarif) => {
        setEditingId(tarif.id);
        setFormData({
            titre: tarif.titre,
            categorie: tarif.categorie,
            prix_citadine: tarif.prix_citadine,
            prix_berline: tarif.prix_berline,
            prix_suv: tarif.prix_suv,
            duree: tarif.duree || '',
            ordre: tarif.ordre,
            populaire: tarif.populaire,
            description: tarif.description,
            liste_details: tarif.liste_details
        });
        // Switch to correct tab context if needed
        if (tarif.categorie === 'Option') setServiceType('option');
        else if (mobilierCategories.includes(tarif.categorie)) setServiceType('mobilier');
        else setServiceType('auto');

        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Force correct category based on current tab
            let finalData = { ...formData };
            if (serviceType === 'option') finalData.categorie = 'Option';
            if (serviceType === 'mobilier' && !mobilierCategories.includes(finalData.categorie)) {
                finalData.categorie = 'Canapé'; // Default fallback
            }

            if (editingId) {
                await pb.collection('tarifs').update(editingId, finalData);
                alert("Modifications enregistrées !");
            } else {
                await pb.collection('tarifs').create(finalData);
                alert("Ajouté avec succès !");
            }
            handleResetForm();
            loadTarifs();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de l'enregistrement.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce tarif ?")) return;
        try {
            await pb.collection('tarifs').delete(id);
            loadTarifs();
        } catch (err) { alert("Erreur suppression"); }
    };

    const handleTogglePopulaire = async (t) => {
        try {
            await pb.collection('tarifs').update(t.id, { populaire: !t.populaire });
            loadTarifs();
        } catch (err) { alert("Erreur mise à jour"); }
    };

    if (loading) return <div>Chargement des tarifs...</div>;

    // determine categories for dropdown based on active tab
    let currentCategories = [];
    if (serviceType === 'auto') currentCategories = autoCategories;
    else if (serviceType === 'mobilier') currentCategories = mobilierCategories;
    else currentCategories = optionCategories;

    return (
        <div className="admin-section fade-in">
            <h2>💰 Gestion des Tarifs & Offres</h2>
            <p className="text-muted">Créez et modifiez vos prestations.</p>

            {/* MAIN TYPE TOGGLE */}
            <div className="service-type-toggle" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button
                    className={`btn ${serviceType === 'auto' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => { setServiceType('auto'); handleResetForm('auto'); }}
                    style={{ flex: 1, padding: '10px', fontSize: '1.1rem' }}
                >
                    🚗 FORMULES AUTO
                </button>
                <button
                    className={`btn ${serviceType === 'mobilier' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => { setServiceType('mobilier'); handleResetForm('mobilier'); }}
                    style={{ flex: 1, padding: '10px', fontSize: '1.1rem' }}
                >
                    🛋️ MOBILIER
                </button>
                <button
                    className={`btn ${serviceType === 'option' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => { setServiceType('option'); handleResetForm('option'); }}
                    style={{ flex: 1, padding: '10px', fontSize: '1.1rem', border: serviceType === 'option' ? 'none' : '1px solid #8b5cf6', color: serviceType === 'option' ? 'white' : '#8b5cf6', backgroundColor: serviceType === 'option' ? '#8b5cf6' : 'transparent' }}
                >
                    🔧 OPTIONS
                </button>
            </div>

            {/* Editing / Creation Form */}
            <div className={`service-card new-service-card full-width-form ${editingId ? 'editing-mode' : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0 }}>
                        {editingId ? '✏️ Modifier le tarif' : (serviceType === 'option' ? '➕ Ajouter une Option' : `➕ Ajouter ${serviceType === 'auto' ? 'Formule Auto' : 'Service Mobilier'}`)}
                    </h3>
                    {editingId && (
                        <button onClick={handleResetForm} className="btn-small" style={{ background: '#cbd5e1', color: '#334155' }}>
                            <FaTimes /> Annuler
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="tarif-form-grid">
                    <div className="form-group">
                        <label>Titre</label>
                        <input
                            value={formData.titre}
                            onChange={e => setFormData({ ...formData, titre: e.target.value })}
                            placeholder={serviceType === 'option' ? "Ex: Siège Bébé" : "Ex: PROPRETÉ PREMIUM"}
                            required
                        />
                    </div>

                    {serviceType !== 'option' && (
                        <div className="form-group">
                            <label>Catégorie</label>
                            <select
                                value={formData.categorie}
                                onChange={e => setFormData({ ...formData, categorie: e.target.value })}
                            >
                                {currentCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="form-group full-width">
                        <label>Sous-titre / Description courte</label>
                        <input
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Ex: Idéal pour un nettoyage en profondeur"
                        />
                    </div>

                    {serviceType !== 'option' && (
                        <div className="form-group full-width">
                            <label>Détails (1 par ligne)</label>
                            <textarea
                                value={formData.liste_details}
                                onChange={e => setFormData({ ...formData, liste_details: e.target.value })}
                                placeholder="- Aspiration..."
                                rows={3}
                                className="details-textarea"
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                            />
                        </div>
                    )}

                    {/* DYNAMIC PRICES & DURATION */}
                    <div className="prices-duration-grid" style={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                        {serviceType === 'auto' ? (
                            <>
                                <div className="form-group">
                                    <label>Prix Citadine (€)</label>
                                    <input type="number" value={formData.prix_citadine} onChange={e => setFormData({ ...formData, prix_citadine: Number(e.target.value) })} placeholder="50" />
                                </div>
                                <div className="form-group">
                                    <label>Prix Berline (€)</label>
                                    <input type="number" value={formData.prix_berline} onChange={e => setFormData({ ...formData, prix_berline: Number(e.target.value) })} placeholder="60" />
                                </div>
                                <div className="form-group">
                                    <label>Prix SUV (€)</label>
                                    <input type="number" value={formData.prix_suv} onChange={e => setFormData({ ...formData, prix_suv: Number(e.target.value) })} placeholder="70" />
                                </div>
                            </>
                        ) : (
                            <div className="form-group">
                                <label>Prix (€)</label>
                                <input
                                    type="number"
                                    value={formData.prix_citadine}
                                    onChange={e => setFormData({ ...formData, prix_citadine: Number(e.target.value) })}
                                    placeholder="Ex: 15"
                                />
                            </div>
                        )}
                        
                        <div className="form-group">
                            <label>Durée (min)</label>
                            <input 
                                type="number" 
                                value={formData.duree} 
                                onChange={e => setFormData({ ...formData, duree: Number(e.target.value) })} 
                                placeholder="Ex: 30" 
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ gridColumn: 'span 3', marginTop: '10px' }}>
                        <button type="submit" className={`save-btn ${editingId ? 'btn-edit' : 'btn-add'}`} style={{ width: '100%' }}>
                            {editingId ? <><FaSave /> Enregistrer les modifications</> : <><FaPlus /> Ajouter maintenant</>}
                        </button>
                    </div>
                </form>
            </div>

            {/* List by Category */}
            {currentCategories.map(cat => {
                const catTarifs = tarifs.filter(t => t.categorie === cat);
                if (catTarifs.length === 0) return null;

                const isAutoCategory = serviceType === 'auto'; // Only 'auto' type gets the 3-column price Layout

                return (
                    <div key={cat} className="category-section">
                        <h3 className="category-title">{serviceType === 'option' ? '🔧 Vos Options' : cat}</h3>
                        <div className="services-grid">
                            {catTarifs.map(t => (
                                <div key={t.id} className={`service-card ${t.populaire ? 'popular-card' : ''} ${editingId === t.id ? 'being-edited' : ''}`}>
                                    <div className="service-header align-start">
                                        <div>
                                            <h4 className="service-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {t.titre}
                                                {t.populaire && <span style={{ fontSize: '0.7rem', background: '#fef3c7', color: '#d97706', padding: '2px 6px', borderRadius: '4px' }}>⭐ Populaire</span>}
                                                {t.duree && <span style={{ fontSize: '0.7rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px' }}>⏱️ {t.duree} min</span>}
                                            </h4>
                                            {t.description && <p className="service-subtitle" style={{ marginBottom: '5px' }}>{t.description}</p>}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <button onClick={() => handleDelete(t.id)} className="delete-btn"><FaTrash /></button>
                                            <button onClick={() => handleEdit(t)} className="edit-btn" style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}><FaEdit /></button>
                                        </div>
                                    </div>

                                    {t.liste_details && (
                                        <div className="details-preview" style={{ fontSize: '0.8rem', padding: '5px', background: '#f1f5f9', marginBottom: '10px', color: '#64748b' }}>
                                            📝 {t.liste_details.split('\n')[0]}... ({t.liste_details.split('\n').length} lignes)
                                        </div>
                                    )}

                                    <div className="prices-grid">
                                        {/* Display logic depending on content type */}
                                        {isAutoCategory ? (
                                            <>
                                                <div className="price-item"><span>Cita.</span><strong>{t.prix_citadine}€</strong></div>
                                                <div className="price-item"><span>Berl.</span><strong>{t.prix_berline}€</strong></div>
                                                <div className="price-item"><span>SUV</span><strong>{t.prix_suv}€</strong></div>
                                            </>
                                        ) : (
                                            <div className="price-item full" style={{ width: '100%', textAlign: 'center' }}>
                                                <span>Prix</span>
                                                <strong style={{ fontSize: '1.2rem' }}>{t.prix_citadine} €</strong>
                                            </div>
                                        )}
                                    </div>

                                    <div className="service-footer">
                                        {serviceType !== 'option' && (
                                            <button onClick={() => handleTogglePopulaire(t)} className={`toggle-btn small ${t.populaire ? 'on' : 'off'}`} style={{ width: '100%' }}>
                                                {t.populaire ? 'Retirer "Populaire"' : 'Mettre en "Populaire"'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AdminServices;
